import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '../context/NotificationContext';

const NotificationCenter = () => {
  const { notifications, unreadCount, refreshNotifications } = useNotificationContext();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverHeight = 400;
      const popoverWidth = 320;
      let top = rect.top;
      if (top + popoverHeight > window.innerHeight - 16) {
        top = window.innerHeight - popoverHeight - 16;
        if (top < 8) top = 8;
      }
      setPopoverStyle({
        top: top,
        left: Math.max(8, rect.left - popoverWidth - 8)
      });
      setTimeout(() => {
        if (popoverRef.current) popoverRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) refreshNotifications();
  }, [isOpen, refreshNotifications]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && isOpen && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = (filename) => {
    setIsOpen(false);
    navigate('/analyse', { state: { sourceFile: filename } });
  };

  const markAllAsRead = async () => {
    await Promise.all(
      notifications.filter(n => n.isNew).map(n => fetch(`/api/analysed-files/${n.name}/mark-read`, { method: 'POST' }))
    );
    refreshNotifications();
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        className={`relative p-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${isOpen ? 'ring-2 ring-blue-400' : ''}`}
        aria-label="Ouvrir le centre de notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
      {isOpen && createPortal(
        <div
          ref={popoverRef}
          tabIndex={0}
          className="fixed z-50 w-80 bg-white rounded-xl shadow-2xl animate-fade-in border border-gray-100 outline-none focus:outline-none"
          style={{ 
            top: popoverStyle.top, 
            left: popoverStyle.left, 
            maxHeight: 400,
            width: 320
          }}
        >
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">Notifications</h3>
            <button
              className="p-1 text-gray-500 hover:text-gray-800 focus:outline-none"
              onClick={() => setIsOpen(false)}
              aria-label="Fermer le centre de notifications"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-4 border-b flex justify-end">
            <button
              className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Tout marquer comme lu
            </button>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 288 }}>
            {!notifications || notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Aucune notification
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.name}
                  className={`p-4 border-b hover:bg-gray-50 cursor-pointer ${
                    (notification.isNew === true || notification.is_new === true) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification.name)}
                >
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="font-medium truncate">Fichier analysé</p>
                      <p className="text-sm text-gray-600 truncate">
                        {notification.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notification.updated_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    {(notification.isNew === true || notification.is_new === true) && (
                      <span className="flex-shrink-0 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        Nouveau
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationCenter; 